import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@/components/ui/sonner";
import { ErrorBlock } from "@/features/questionnaire/components/QueryState";

import { useFormationGroupQuery, useUpdateFormationGroup } from "../api";

import { fieldErrors } from "../lib/errors";

import { FormationGroupForm } from "./FormationGroupForm";

export function FormationGroupEdit({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const group = useFormationGroupQuery(id);
  const updateGroup = useUpdateFormationGroup(id);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  // A failed fetch used to render an empty page with no message, where the
  // Django screens fail visibly.
  if (group.error) return <ErrorBlock error={group.error} />;
  if (!group.data) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-foreground">
        {t("formationGroups.form.editTitle")}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t("formationGroups.subtitle")}
      </p>

      <FormationGroupForm
        // The stored set, never the default — editing must not silently reset
        // a group to whatever `available_in_demo` happens to hold today.
        initialName={group.data.name}
        initialGoalIds={group.data.goals ?? []}
        submitLabel={t("formationGroups.save")}
        submitting={updateGroup.isPending}
        serverErrors={serverErrors}
        onCancel={() => navigate({ to: "/formation-groups/$id", params: { id } })}
        onSubmit={(values) =>
          updateGroup.mutate(values as never, {
            onSuccess: () => {
              toast.success(
                t("formationGroups.toasts.updated", { name: values.name }),
              );
              navigate({ to: "/formation-groups/$id", params: { id } });
            },
            onError: (error) => {
              const detail = fieldErrors(error);
              if (detail) setServerErrors(detail);
              else toast.error(t("formationGroups.error"));
            },
          })
        }
      />
    </div>
  );
}
